import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translateLeague } from '../utils/leagueNames';

function Sidebar({
  categories,
  leagues,
  selectedCategory,
  selectedLeague,
  onCategoryChange,
  onLeagueChange,
  stats,
  oddsFormat,
  onOddsFormatChange,
  onShowTagInfo,
  user,
  onLogout,
  enabledGrades,
  onGradeToggle
}) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);

  function formatCategory(category) {
    if (category === 'all') return t('common:allPicks');

    const names = {
      // BTTS categories
      '1_ML_Only': t('dashboard:catMLOnly'),
      '1_ML_All': t('dashboard:catAllML'),
      '2_ML_H2H_BTTS': t('dashboard:catH2HBTTS'),
      '2_Strong_Favorite': t('dashboard:catStrongFavorite'),
      '3_ML_COMBINED_GPG': t('dashboard:catHighGPG'),
      '3_Home_Banker': t('dashboard:catHomeBanker'),
      '4_VETO_APPROVED': t('dashboard:catVetoApproved'),
      '4_Upset_Alert': t('dashboard:catUpsetAlert'),
      '5_Form_Backed': t('dashboard:catFormBacked'),
      // Draw categories
      '2_Odds_Balanced': t('dashboard:catOddsBalanced'),
      '3_Close_Contest': t('dashboard:catCloseContest'),
      '4_Defensive_Draw': t('dashboard:catDefensiveDraw'),
      '5_Form_Backed_Draw': t('dashboard:catFormBackedDraw'),
      '6_Analyzer_Profitable': t('dashboard:catAnalyzerPick'),
      '7_DRAW_CONVICTION': t('dashboard:catConviction'),
      // Correct Score categories
      '2_Performance': t('dashboard:catPerformance'),
      '3_Sharp_Pick': t('dashboard:catSharpPick'),
      '4_Premium': t('dashboard:catPremium'),
      // Over 2.5 categories
      '2_ML_HIGH_GPG': t('dashboard:catHighGPG'),
      '3_ML_OVER25_PCT': t('dashboard:catOver25Pct'),
      // HTFT categories
      '2_HT_Leader': t('dashboard:catHTLeader'),
      '3_Comeback': t('dashboard:catComeback'),
      '4_Draw_Draw': t('dashboard:catDrawDraw'),
      // Ultimate categories
      '1_Overlap_Pick': t('dashboard:catOverlapPick'),
      '2_Confirmed': t('dashboard:catConfirmed'),
      '3_High_Conviction': t('dashboard:catHighConviction'),
      '4_Premium': t('dashboard:catPremium'),
    };

    return names[category] || category.replace(/^\d+_/, '').replace(/_/g, ' ');
  }

  function formatLeague(league) {
    if (league === 'all') return t('common:allLeagues');
    return translateLeague(league, i18n.language);
  }

  return (
    <aside className="sidebar w-64 min-h-screen px-4 overflow-y-auto">
      {/* Stats Summary */}
      <div className="mb-8">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          {t('dashboard:overview')}
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <StatMini label={t('dashboard:total')} value={stats.total} />
          <StatMini label={t('dashboard:completed')} value={stats.completed} />
          <StatMini label={t('dashboard:wins')} value={stats.wins} className="text-win" />
          <StatMini
            label={t('dashboard:winRate')}
            value={stats.completed > 0 ? `${((stats.wins / stats.completed) * 100).toFixed(0)}%` : '-'}
            className={stats.completed > 0 && stats.wins / stats.completed > 0.5 ? 'text-win' : 'text-loss'}
          />
        </div>
      </div>

      {/* Grade Filter */}
      {enabledGrades && onGradeToggle && (
        <div className="mb-8">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {t('dashboard:filterByGrade')}
          </h3>
          <div className="flex gap-1.5">
            {['A+', 'A', 'B', 'C', 'D'].map(grade => {
              const isEnabled = enabledGrades[grade];
              const gradeColors = {
                'A+': { active: 'bg-emerald-500/30 text-emerald-400 border-emerald-500/50', inactive: 'bg-emerald-500/10 text-emerald-600 border-emerald-800' },
                'A': { active: 'bg-green-500/30 text-green-400 border-green-500/50', inactive: 'bg-green-500/10 text-green-600 border-green-800' },
                'B': { active: 'bg-yellow-500/30 text-yellow-400 border-yellow-500/50', inactive: 'bg-yellow-500/10 text-yellow-600 border-yellow-800' },
                'C': { active: 'bg-orange-500/30 text-orange-400 border-orange-500/50', inactive: 'bg-orange-500/10 text-orange-600 border-orange-800' },
                'D': { active: 'bg-red-500/30 text-red-400 border-red-500/50', inactive: 'bg-red-500/10 text-red-600 border-red-800' },
              };
              const colors = gradeColors[grade];
              return (
                <button
                  key={grade}
                  onClick={() => onGradeToggle(grade)}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                    isEnabled ? colors.active : colors.inactive + ' opacity-50'
                  }`}
                  title={isEnabled ? t('dashboard:hideGrade', { grade }) : t('dashboard:showGrade', { grade })}
                >
                  {grade}
                </button>
              );
            })}
          </div>
          {Object.values(enabledGrades).some(v => !v) && (
            <button
              onClick={() => ['A+', 'A', 'B', 'C', 'D'].forEach(g => !enabledGrades[g] && onGradeToggle(g))}
              className="mt-2 w-full text-xs text-gray-500 hover:text-accent transition-colors"
            >
              {t('dashboard:resetFilters')}
            </button>
          )}
        </div>
      )}

      {/* Categories */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {t('dashboard:categories')}
          </h3>
          <button
            onClick={onShowTagInfo}
            className="text-xs text-gray-500 hover:text-accent transition-colors flex items-center gap-1"
            title="Learn about tags"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('dashboard:info')}
          </button>
        </div>
        <nav className="space-y-1">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => onCategoryChange(category)}
              className={`sidebar-item w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                selectedCategory === category
                  ? 'active text-accent'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate">{formatCategory(category)}</span>
                {category !== 'all' && (
                  <CategoryTag category={category} />
                )}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Leagues */}
      <div className="mb-8">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          {t('dashboard:leagues')}
        </h3>
        <nav className="space-y-1 max-h-64 overflow-y-auto">
          {leagues.map(league => (
            <button
              key={league}
              onClick={() => onLeagueChange(league)}
              className={`sidebar-item w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                selectedLeague === league
                  ? 'active text-accent'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                {league !== 'all' && (
                  <span className="w-2 h-2 rounded-full bg-accent/50"></span>
                )}
                <span className="truncate">{formatLeague(league)}</span>
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Account */}
      <div className="pt-4 mt-4 border-t border-anthracite-700">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          {t('dashboard:account')}
        </h3>
        <div className="space-y-1">
          {user?.email && (
            <div className="text-xs text-gray-400 truncate px-3 mb-2">
              {user.email}
            </div>
          )}
          <Link
            to="/account"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-anthracite-800 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {t('dashboard:myAccount')}
          </Link>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-anthracite-800 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {t('common:signOut')}
          </button>
        </div>
      </div>

      {/* Settings */}
      <div className="pt-4 mt-4 border-t border-anthracite-700">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          {t('dashboard:settings')}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 block mb-2">{t('dashboard:oddsFormat')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => onOddsFormatChange('decimal')}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  oddsFormat === 'decimal'
                    ? 'bg-accent/20 text-accent border border-accent/30'
                    : 'bg-anthracite-800 text-gray-400 border border-anthracite-700 hover:border-accent/30'
                }`}
              >
                {t('dashboard:decimal')}
              </button>
              <button
                onClick={() => onOddsFormatChange('fractional')}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  oddsFormat === 'fractional'
                    ? 'bg-accent/20 text-accent border border-accent/30'
                    : 'bg-anthracite-800 text-gray-400 border border-anthracite-700 hover:border-accent/30'
                }`}
              >
                {t('dashboard:fractional')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function StatMini({ label, value, className = '' }) {
  return (
    <div className="stat-card p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`stat-number text-lg ${className}`}>{value}</div>
    </div>
  );
}

function CategoryTag({ category }) {
  const tagInfo = getCategoryTagInfo(category);
  if (!tagInfo) return null;

  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${tagInfo.style}`}>
      {tagInfo.label}
    </span>
  );
}

function getCategoryTagInfo(category) {
  const mapping = {
    // BTTS categories
    '1_ML_Only': { label: 'ML', style: 'bg-blue-500/20 text-blue-400' },
    '1_ML_All': { label: 'ML', style: 'bg-blue-500/20 text-blue-400' },
    '2_ML_H2H_BTTS': { label: 'H2H', style: 'bg-purple-500/20 text-purple-400' },
    '2_Strong_Favorite': { label: 'FAV', style: 'bg-purple-500/20 text-purple-400' },
    '3_ML_COMBINED_GPG': { label: 'GPG', style: 'bg-orange-500/20 text-orange-400' },
    '3_Home_Banker': { label: 'HOME', style: 'bg-orange-500/20 text-orange-400' },
    '4_VETO_APPROVED': { label: 'VETO', style: 'bg-green-500/20 text-green-400' },
    '4_Upset_Alert': { label: 'UPSET', style: 'bg-red-500/20 text-red-400' },
    '5_Form_Backed': { label: 'FORM', style: 'bg-teal-500/20 text-teal-400' },
    // Draw categories
    '1_ML_All': { label: 'ML', style: 'bg-blue-500/20 text-blue-400' },
    '2_Odds_Balanced': { label: 'BAL', style: 'bg-cyan-500/20 text-cyan-400' },
    '3_Close_Contest': { label: 'CLOSE', style: 'bg-indigo-500/20 text-indigo-400' },
    '4_Defensive_Draw': { label: 'DEF', style: 'bg-slate-500/20 text-slate-400' },
    '5_Form_Backed_Draw': { label: 'FORM', style: 'bg-teal-500/20 text-teal-400' },
    '6_Analyzer_Profitable': { label: 'PROF', style: 'bg-amber-500/20 text-amber-400' },
    '7_DRAW_CONVICTION': { label: 'CONV', style: 'bg-emerald-500/20 text-emerald-400' },
    // Correct Score categories
    '2_Performance': { label: 'PERF', style: 'bg-lime-500/20 text-lime-400' },
    '3_Sharp_Pick': { label: 'SHARP', style: 'bg-yellow-500/20 text-yellow-400' },
    '4_Premium': { label: 'PREM', style: 'bg-amber-500/20 text-amber-400' },
    // Over 2.5 categories
    '2_ML_HIGH_GPG': { label: 'GPG', style: 'bg-orange-500/20 text-orange-400' },
    '3_ML_OVER25_PCT': { label: 'O25', style: 'bg-sky-500/20 text-sky-400' },
    // HTFT categories
    '2_HT_Leader': { label: 'LEAD', style: 'bg-blue-500/20 text-blue-400' },
    '3_Comeback': { label: 'CBACK', style: 'bg-red-500/20 text-red-400' },
    '4_Draw_Draw': { label: 'DD', style: 'bg-gray-500/20 text-gray-400' },
    // Ultimate categories
    '1_Overlap_Pick': { label: 'OVR', style: 'bg-violet-500/20 text-violet-400' },
    '2_Confirmed': { label: 'CONF', style: 'bg-blue-500/20 text-blue-400' },
    '3_High_Conviction': { label: 'HI-C', style: 'bg-amber-500/20 text-amber-400' },
    '4_Premium': { label: 'PREM', style: 'bg-emerald-500/20 text-emerald-400' },
  };
  return mapping[category] || null;
}

function getTagStyle(tag) {
  const styles = {
    // BTTS / Outcome tags
    'ML': 'bg-blue-500/20 text-blue-400',
    'H2H': 'bg-purple-500/20 text-purple-400',
    'GPG': 'bg-orange-500/20 text-orange-400',
    'VETO': 'bg-green-500/20 text-green-400',
    'FAV': 'bg-purple-500/20 text-purple-400',
    'HOME': 'bg-orange-500/20 text-orange-400',
    'UPSET': 'bg-red-500/20 text-red-400',
    'FORM': 'bg-teal-500/20 text-teal-400',
    // Draw tags
    'BAL': 'bg-cyan-500/20 text-cyan-400',
    'CLOSE': 'bg-indigo-500/20 text-indigo-400',
    'DEF': 'bg-slate-500/20 text-slate-400',
    'PROF': 'bg-amber-500/20 text-amber-400',
    'CONV': 'bg-emerald-500/20 text-emerald-400',
    // Correct Score tags
    'SHARP': 'bg-yellow-500/20 text-yellow-400',
    'PREM': 'bg-amber-500/20 text-amber-400',
    'PERF': 'bg-lime-500/20 text-lime-400',
    // Over 2.5 tags
    'O25': 'bg-sky-500/20 text-sky-400',
    // HT/FT tags
    'LEAD': 'bg-blue-500/20 text-blue-400',
    'CBACK': 'bg-red-500/20 text-red-400',
    'DD': 'bg-gray-500/20 text-gray-400',
    // Ultimate tags
    'DRAW+CS': 'bg-emerald-500/20 text-emerald-400',
    'CS+': 'bg-amber-500/20 text-amber-400',
    'DRAW+': 'bg-cyan-500/20 text-cyan-400',
    '3ML': 'bg-violet-500/20 text-violet-400',
    'CLEAN': 'bg-green-500/20 text-green-400',
    'XVAL': 'bg-lime-500/20 text-lime-400',
  };
  return styles[tag] || 'bg-anthracite-700 text-gray-400';
}

export default Sidebar;
